import React from 'react';

/**
 * CAFFRYX ABOUT US - REFINED SLIDER EDITION
 * Theme: Refined Professionalism
 * Colors: Aqua Haze (#F0F4F8), River Bed (#4A5568), Cerulean Blue (#2B6CB0)
 */
const About = ({ onSectionChange }) => {
  return (
    <div className="min-h-screen bg-[#F0F4F8] text-[#4A5568] pt-20 font-sans overflow-x-hidden">
      
      {/* HERO SECTION */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h1 className="text-6xl md:text-9xl font-black mb-8 tracking-tighter text-[#020617] uppercase leading-[0.8]">
            About <span className="text-[#2B6CB0]">Us.</span>
          </h1>
          <p className="text-xl md:text-2xl text-[#4A5568] max-w-2xl mx-auto mb-12 font-medium leading-relaxed italic">
            A high-performance studio built for those who value precision over noise. 
          </p>
        </div>
      </section>

      {/* REFINED SMALL SLIDER */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-[2rem] bg-white p-4 shadow-xl shadow-[#2B6CB0]/5 border border-white">
            <div className="flex space-x-4 animate-scroll-fast hover:pause-scroll transition-all">
              {/* Image Set */}
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="min-w-[200px] md:min-w-[300px] h-[250px] md:h-[350px] rounded-2xl overflow-hidden shadow-inner">
                  <img 
                    src={`https://images.unsplash.com/photo-${1500000000000 + (i * 123456)}?auto=format&fit=crop&w=600&q=80`} 
                    alt="Caffryx Studio Aesthetic" 
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                  />
                </div>
              ))}
              {/* Duplicate for Infinite Effect */}
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={`dup-${i}`} className="min-w-[200px] md:min-w-[300px] h-[250px] md:h-[350px] rounded-2xl overflow-hidden shadow-inner">
                  <img 
                    src={`https://images.unsplash.com/photo-${1500000000000 + (i * 123456)}?auto=format&fit=crop&w=600&q=80`} 
                    alt="Caffryx Studio Aesthetic" 
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <style dangerouslySetInnerHTML={{ __html: `
          @keyframes scroll-fast {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-scroll-fast {
            display: flex;
            width: calc(200px * 12 + 48px); /* mobile widths */
            animation: scroll-fast 30s linear infinite;
          }
          @media (min-width: 768px) {
            .animate-scroll-fast { 
              width: calc(300px * 12 + 48px); /* desktop widths */
            }
          }
          .pause-scroll:hover {
            animation-play-state: paused;
          }
        `}} />
      </section>

      {/* THE CAFFRYX STANDARD */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-3 gap-20">
            <div className="lg:col-span-1">
              <h2 className="text-5xl font-black text-[#020617] uppercase tracking-tighter leading-none mb-6">
                The <br/><span className="text-[#2B6CB0]">Standard</span>
              </h2>
              <p className="text-lg font-medium leading-relaxed">
                We treat design as a business instrument. Every grid, pixel, and line of code is measured against the impact it creates for your brand.
              </p>
            </div>
            
            <div className="lg:col-span-2 grid md:grid-cols-2 gap-x-8 gap-y-12">
              {[
                { title: "Radical Clarity", desc: "We strip away the decorative to find the core message that drives action." },
                { title: "Sustained Velocity", desc: "We operate in focused, iterative cycles. No bloated processes—just consistent momentum." },
                { title: "Obsessive Craft", desc: "Bespoke systems and modular architecture built to evolve with your scale." },
                { title: "Direct Collaboration", desc: "No layers. You work directly with the practitioners shaping your vision." }
              ].map((value, i) => (
                <div key={i} className="group">
                  <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-[#2B6CB0] mb-3 flex items-center">
                    <span className="w-4 h-[1px] bg-[#2B6CB0] mr-2"></span>
                    {value.title}
                  </h4>
                  <p className="text-base text-[#4A5568] leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* OPERATING RHYTHM */}
      <section className="py-20 bg-[#020617] text-white rounded-[3rem] mx-4 md:mx-8">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h3 className="text-[#2B6CB0] text-[10px] font-black uppercase tracking-[0.4em] mb-6">Operating Rhythm</h3>
          <p className="text-xl md:text-2xl leading-relaxed italic font-light opacity-90">
            "Caffryx operates on a senior-only model. Strategy, design, and engineering sit together so decisions travel cleanly from thinking to making. Every engagement has a dedicated lead—ensuring zero friction from brief to launch."
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-32 text-center">
        <button 
          onClick={() => onSectionChange('contact')}
          className="bg-[#2B6CB0] text-white px-20 py-6 rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-[#020617] transition-all shadow-2xl shadow-[#2B6CB0]/20 active:scale-95"
        >
          Start A Conversation
        </button>
      </section>
    </div>
  );
};

export default About;